import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, Lock, Eye, Database, Users, Cookie, Clock, Baby, RefreshCw, Mail } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

const BRAND_NAME = "FinAI";
const CONTACT_EMAIL = "support@finaigpt.com";
const OPERATOR_NAME = "FinAI";

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const sectionIcons = {
  1: Eye,
  2: Database,
  3: Lock,
  4: Users,
  5: Shield,
  6: Cookie,
  7: Clock,
  8: Baby,
  9: RefreshCw,
  10: Mail,
};

export default function Privacy() {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-emerald-500/8 to-teal-500/5 blur-3xl -top-32 -left-32 animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute w-72 h-72 rounded-full bg-gradient-to-r from-cyan-500/5 to-blue-500/5 blur-3xl bottom-1/4 -right-20 animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/80 backdrop-blur-xl border-b border-white/5">
        <div className="h-[2px] bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" className="text-gray-300 hover:text-white" onClick={handleBack}>
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back
          </Button>
          <motion.div 
            className="flex items-center gap-2 text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Shield className="w-4 h-4" />
            <span className="text-xs font-medium">Your Privacy Matters</span>
          </motion.div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-10 md:py-14 max-w-4xl relative z-10">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
            Privacy Policy
          </h1>
          <p className="text-gray-400 mb-8 text-sm md:text-base">How we collect, use, and protect your information</p>
        </motion.div>
        
        <div className="space-y-6">
          {/* Overview Card */}
          <AnimatedSection delay={0.1}>
            <div className="bg-gray-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/5 hover:border-emerald-500/20 transition-colors duration-300">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Overview</h2>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                {BRAND_NAME} ("we", "us", or "our") is operated by {OPERATOR_NAME}. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our 
                financial planning and advisory platform.
              </p>
            </div>
          </AnimatedSection>

          {/* Section 1 */}
          <AnimatedSection delay={0.05}>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <Eye className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">1. Information We Collect</h2>
              </div>
              
              <div className="ml-11 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold mb-2 text-emerald-400 uppercase tracking-wider">Personal Information</h3>
                  <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                    <li>Name and email address (for account creation)</li>
                    <li>Password (stored in encrypted form)</li>
                    <li>Phone number (optional, for account recovery)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-2 text-emerald-400 uppercase tracking-wider">Financial Information</h3>
                  <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                    <li>Income, expenses, and savings data you voluntarily enter</li>
                    <li>Investment portfolio details</li>
                    <li>Financial goals and plans</li>
                    <li>Insurance and loan information</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-2 text-emerald-400 uppercase tracking-wider">For Advisor Accounts (B2B)</h3>
                  <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                    <li>Business/professional details</li>
                    <li>Client information you manage through our platform</li>
                    <li>Payment and subscription information</li>
                  </ul>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Section 2 */}
          <AnimatedSection>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <Database className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">2. How We Use Your Information</h2>
              </div>
              <ul className="list-disc pl-16 space-y-1.5 text-gray-300 text-sm">
                <li>To provide personalized financial planning tools and AI-powered recommendations</li>
                <li>To generate financial reports and analysis</li>
                <li>To process payments for subscription services</li>
                <li>To communicate important updates about our service</li>
                <li>To improve and optimize our platform</li>
                <li>To comply with legal obligations</li>
              </ul>
            </div>
          </AnimatedSection>

          {/* Section 3 */}
          <AnimatedSection>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <Lock className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">3. Data Storage and Security</h2>
              </div>
              <div className="ml-11">
                <p className="mb-3 text-gray-300 text-sm">We implement industry-standard security measures to protect your data:</p>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                  <li>All data is encrypted in transit using SSL/TLS</li>
                  <li>Passwords are hashed using bcrypt encryption</li>
                  <li>Database access is restricted and monitored</li>
                  <li>Regular security audits and updates</li>
                </ul>
                <div className="mt-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3">
                  <p className="text-yellow-400/80 text-xs">
                    Note: While we take all reasonable precautions, no method of transmission over the 
                    Internet is 100% secure. We cannot guarantee absolute security of your data.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Section 4 */}
          <AnimatedSection>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <Users className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">4. Data Sharing</h2>
              </div>
              <div className="ml-11">
                <p className="mb-3 text-gray-300 text-sm">We do NOT sell your personal information. We may share data only in these cases:</p>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                  <li><strong className="text-white">Payment Processing:</strong> With Razorpay for processing subscription payments</li>
                  <li><strong className="text-white">AI Services:</strong> With OpenAI/AI providers to generate recommendations (anonymized where possible)</li>
                  <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong className="text-white">With Your Consent:</strong> For any other purpose with your explicit permission</li>
                </ul>
              </div>
            </div>
          </AnimatedSection>

          {/* Section 5 */}
          <AnimatedSection>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <Shield className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">5. Your Rights</h2>
              </div>
              <div className="ml-11">
                <p className="mb-3 text-gray-300 text-sm">You have the following rights regarding your data:</p>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                  <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
                  <li><strong className="text-white">Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong className="text-white">Deletion:</strong> Request deletion of your account and data</li>
                  <li><strong className="text-white">Export:</strong> Download your data in a portable format</li>
                  <li><strong className="text-white">Opt-out:</strong> Unsubscribe from marketing communications</li>
                </ul>
                <p className="mt-3 text-sm text-gray-300">
                  To exercise these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-400 hover:underline">{CONTACT_EMAIL}</a>
                </p>
              </div>
            </div>
          </AnimatedSection>

          {/* Section 6 */}
          <AnimatedSection>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <Cookie className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">6. Cookies and Tracking</h2>
              </div>
              <p className="ml-11 text-gray-300 text-sm leading-relaxed">
                We use essential cookies to maintain your session and remember your preferences. 
                We do not use third-party advertising cookies. You can disable cookies in your 
                browser settings, but this may affect functionality.
              </p>
            </div>
          </AnimatedSection>

          {/* Section 7 */}
          <AnimatedSection>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <Clock className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">7. Data Retention</h2>
              </div>
              <p className="ml-11 text-gray-300 text-sm leading-relaxed">
                We retain your data for as long as your account is active. After account deletion, 
                we may retain certain data for up to 90 days for backup purposes, after which it 
                is permanently deleted. Financial transaction records may be retained longer as 
                required by law.
              </p>
            </div>
          </AnimatedSection>

          {/* Section 8 */}
          <AnimatedSection>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <Baby className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">8. Children's Privacy</h2>
              </div>
              <p className="ml-11 text-gray-300 text-sm leading-relaxed">
                Our service is not intended for individuals under 18 years of age. We do not 
                knowingly collect personal information from children.
              </p>
            </div>
          </AnimatedSection>

          {/* Section 9 */}
          <AnimatedSection>
            <div className="group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
                  <RefreshCw className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">9. Changes to This Policy</h2>
              </div>
              <p className="ml-11 text-gray-300 text-sm leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any 
                changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>
            </div>
          </AnimatedSection>

          {/* Contact Card */}
          <AnimatedSection>
            <div className="bg-gray-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/5 hover:border-emerald-500/20 transition-colors duration-300">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">10. Contact Us</h2>
              </div>
              <p className="text-gray-300 text-sm mb-3">
                If you have any questions about this Privacy Policy or our data practices, please contact:
              </p>
              <div className="space-y-1.5 text-sm">
                <p className="text-gray-300"><strong className="text-white">Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-400 hover:underline">{CONTACT_EMAIL}</a></p>
                <p className="text-gray-300"><strong className="text-white">Operated by:</strong> {OPERATOR_NAME}</p>
              </div>
            </div>
          </AnimatedSection>

          <motion.p 
            className="text-xs text-gray-500 mt-8 pt-4 border-t border-gray-800"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </div>
      </div>
    </div>
  );
}
